import { ArrowLeft, FileText } from 'lucide-react'

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <a
            href="/"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </a>
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-primary-600" />
            <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
          </div>
          <p className="text-gray-600 mt-2">Last updated: April 11, 2026</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-md p-8 space-y-8">

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing or using Nearbies (the "Service"), operated by Adhithi Ltd. (trading as TillTech and Nearbies) ("we," "us," or "our"),
              you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms,
              you may not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 leading-relaxed">
              Nearbies is a platform that connects customers with local stores for online ordering,
              pickup, and delivery services. We facilitate transactions between customers and merchants
              but are not party to the actual sale of goods or services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.1 Account Creation</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              To use certain features of the Service, you must create an account. You agree to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your password and account</li>
              <li>Notify us immediately of any unauthorized use</li>
              <li>Be responsible for all activities under your account</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.2 Age Requirement</h3>
            <p className="text-gray-700 leading-relaxed">
              You must be at least 13 years old to create an account. If you are under 18, you
              represent that you have your parent or guardian's permission to use the Service.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3.3 Account Termination</h3>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to suspend or terminate your account at any time for violations
              of these Terms or for any other reason at our sole discretion.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Orders and Payments</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.1 Placing Orders</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              When you place an order through the Service:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>You are making an offer to purchase goods from the merchant</li>
              <li>The merchant has the right to accept or decline your order</li>
              <li>We are not responsible for merchant's acceptance, fulfillment, or quality</li>
              <li>All prices are as displayed at the time of order</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.2 Payment Options</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Payment for orders can be made through:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Cash on Delivery:</strong> Pay the merchant directly when receiving your order</li>
              <li><strong>TillTech Card Terminal:</strong> If the merchant has integrated card payment, you may pay via card at pickup or delivery</li>
              <li><strong>Payment Links:</strong> Some merchants may share payment links for online payment</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              Nearbies does not directly process payments. All payment arrangements are between you and the merchant.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.3 Refunds and Cancellations</h3>
            <p className="text-gray-700 leading-relaxed">
              Refund and cancellation policies are set by individual merchants. We facilitate refunds
              but are not responsible for merchant refund decisions. Contact the merchant directly for
              order-specific issues.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. User Conduct</h2>
            <p className="text-gray-700 leading-relaxed mb-3">You agree NOT to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Violate any laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Transmit harmful code or viruses</li>
              <li>Harass, abuse, or harm other users or merchants</li>
              <li>Use the Service for fraudulent purposes</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Scrape, crawl, or extract data from the Service</li>
              <li>Impersonate another person or entity</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed">
              All content, features, and functionality of the Service, including but not limited to
              text, graphics, logos, and software, are owned by Adhithi Ltd. or our licensors and
              are protected by copyright, trademark, and other intellectual property laws. You may
              not copy, modify, distribute, or create derivative works without our express permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Loyalty Rewards Program</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Nearbies offers a loyalty rewards program ("Program") with the following terms:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Points are earned on eligible purchases at participating merchants</li>
              <li>Points have no cash value and cannot be transferred</li>
              <li>We reserve the right to modify or terminate the Program at any time</li>
              <li>Points may expire after a period of inactivity (as specified by merchant)</li>
              <li>Fraudulent point accumulation will result in account termination</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Disclaimer of Warranties</h2>
            <p className="text-gray-700 leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE,
              OR ERROR-FREE. WE MAKE NO WARRANTIES REGARDING THE QUALITY, ACCURACY, OR AVAILABILITY
              OF MERCHANT PRODUCTS OR SERVICES.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, ADHITHI LTD. SHALL NOT BE LIABLE FOR:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Indirect, incidental, special, or consequential damages</li>
              <li>Loss of profits, data, or goodwill</li>
              <li>Merchant's failure to fulfill orders</li>
              <li>Quality or safety of products purchased through the Service</li>
              <li>Any damages exceeding the amount you paid us in the last 12 months</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify and hold harmless Adhithi Ltd., its officers, directors, employees,
              and agents from any claims, liabilities, damages, losses, or expenses arising from your
              use of the Service, violation of these Terms, or violation of any third-party rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Dispute Resolution</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">11.1 Governing Law</h3>
            <p className="text-gray-700 leading-relaxed">
              These Terms are governed by the laws of England and Wales, without regard to conflict
              of law principles.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">11.2 Dispute Resolution Process</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              In the event of a dispute:
            </p>
            <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
              <li>First, contact us at support@nearbies.co.uk to resolve informally</li>
              <li>If unresolved, we agree to good-faith mediation</li>
              <li>As a last resort, disputes will be resolved in the courts of England and Wales</li>
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify you of significant
              changes via email or through the Service. Your continued use after changes constitutes
              acceptance of the modified Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              You may terminate your account at any time by contacting us or using the account deletion
              feature in the app. We may terminate or suspend your access immediately, without notice,
              for violations of these Terms or for any other reason.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Severability</h2>
            <p className="text-gray-700 leading-relaxed">
              If any provision of these Terms is found to be unenforceable, the remaining provisions
              will continue in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Contact Information</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions about these Terms, please contact us:
              </p>
              <ul className="text-gray-700 space-y-2">
                <li><strong>Email:</strong> <a href="mailto:support@nearbies.co.uk" className="text-primary-600 underline">support@nearbies.co.uk</a></li>
                <li><strong>Legal Inquiries:</strong> <a href="mailto:legal@nearbies.co.uk" className="text-primary-600 underline">legal@nearbies.co.uk</a></li>
              </ul>
            </div>
          </section>

          <section className="border-t pt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Company Information</h3>
            <p className="text-gray-700 leading-relaxed">
              Adhithi Ltd. (trading as TillTech and Nearbies)<br />
              Registered in England and Wales<br />
              Contact: legal@nearbies.co.uk
            </p>
          </section>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
            <p className="text-sm text-blue-900">
              <strong>Important:</strong> By using Nearbies, you acknowledge that you have read,
              understood, and agree to be bound by these Terms of Service and our Privacy Policy.
            </p>
          </div>

        </div>
      </main>
    </div>
  )
}

export default TermsOfService
